import { TwoSidedLadderBody } from './TwoSidedLadder';
import type { VenueBook } from '../ValueDashboard/types';
import { usBookCurrent, usBookForOutcome } from '@/lib/polymarketUs/gameMapping';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { useUsLadderBook } from '../ValueDashboard/us/useUsLadderBook';
import type { UsLineCell } from './usLineCell';

/** Native US outcomes beside the existing detailed two-sided K/P books.
 * Mounted for an expanded exact line only; clicking only seeds its ticket. */
export function UsLineBooks({ cell, onTrade, labels }: {
  cell: UsLineCell; onTrade?: (side: 'yes' | 'no', cents: number) => void;
  labels?: { yes: string; no: string };
}) {
  const slug = 'quote' in cell ? cell.quote.marketSlug : null;
  const book = useUsLadderBook(slug);
  const now = useSharedNow(1000, book?.state === 'current');
  if (cell.state === 'absent') return null;
  if (cell.state === 'awaiting') return <section className="min-w-[26rem] flex-1" aria-label="Polymarket US YES and NO book"><h4 className="text-xs font-semibold uppercase">Polymarket US <span className="text-amber-400">Quote unavailable</span></h4></section>;
  const current = book !== null && book.marketSlug === slug && usBookCurrent(book, now);
  const status = current ? 'Live' : book?.marketSlug === slug && book.state !== 'unavailable' ? 'Stale' : 'Unavailable';
  // The existing two-sided renderer consumes bid arrays for both displayed
  // outcomes. This adapter stays local to presentation; it never enters the
  // market store, a Kalshi target, account totals or an execution request.
  const yes = current ? usBookForOutcome(book, cell.outcomeFor('yes')) : null;
  const no = current ? usBookForOutcome(book, cell.outcomeFor('no')) : null;
  const displayBook: VenueBook | null = current ? {
    marketTicker: cell.quote.marketSlug, priceCents: null,
    volume: 0, liquidity: 0, yes: yes?.bids ?? [], no: no?.bids ?? [],
  } : null;
  return <section className="min-w-[26rem] flex-1" aria-label="Polymarket US YES and NO book">
    <h4 className="mb-1 text-xs font-semibold uppercase">Polymarket US <span className={current ? 'text-emerald-400' : 'text-amber-400'}>{status}</span></h4>
    <TwoSidedLadderBody venue="polymarket_us" kalshi={displayBook}
      yesLabel={labels?.yes ?? 'YES'} noLabel={labels?.no ?? 'NO'}
      userOrders={null} availability={current ? 'active' : 'stale'} makerRebatePct={0}
      presentation={{ depth: 5, layout: 'narrow' }}
      onLevelClick={(side, _kind, cents) => {
        if (book && usBookCurrent(book, Date.now())) onTrade?.(side, cents);
      }} />
  </section>;
}
